package com.wintermar.inspection.feature.presentation.inspection.landing

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.wintermar.inspection.core.data.Uuid
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.ui.component.datetime.util.DateTimeUtil
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarController
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarPosition
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarVariant
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.service.SubmitRetryService
import com.wintermar.inspection.feature.domain.usecase.inspection.CreateNewInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DeleteInspectionReportUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DeleteInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.GetInspectionDetailUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.GetListInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.StartInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.SyncInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListTemplateOptionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListVesselUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetProfileUseCase
import com.wintermar.inspection.feature.domain.usecase.template.GetSimpleListTemplateUseCase
import com.wintermar.inspection.feature.util.expect_init.processAfterSuccessStartInspection
import com.wintermar.inspection.feature.util.expect_init.processGetProfile
import com.wintermar.inspection.graphql.type.InspectionStatus
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class InspectionLandingViewModel(
    private val getListInspectionUseCase: GetListInspectionUseCase,
    private val getSimpleListTemplateUseCase: GetSimpleListTemplateUseCase,
    private val createNewInspectionUseCase: CreateNewInspectionUseCase,
    private val startInspectionUseCase: StartInspectionUseCase,
    private val getInspectionDetailUseCase: GetInspectionDetailUseCase,
    private val getListTemplateOptionUseCase: GetListTemplateOptionUseCase,
    private val getListVesselUseCase: GetListVesselUseCase,
    private val submitRetryService: SubmitRetryService,
    private val deleteInspectionUseCase: DeleteInspectionUseCase,
    private val getProfileUseCase: GetProfileUseCase,
    private val syncInspectionUseCase: SyncInspectionUseCase,
    private val deleteInspectionReportsUseCase: DeleteInspectionReportUseCase
) : ViewModel() {
    private val _navigationEvent = Channel<InspectionLandingEvent>()
    val navigationEvent = _navigationEvent.receiveAsFlow()

    val state = InspectionLandingState(
        onGetData = { page, isRefreshData ->
            handleOnGetData(page, isRefreshData)
        },
        onCreateNewInspection = { templateId, templateName ->
            startInspection(templateId, templateName)
        },
        onShowSuccessToast = { message ->
            onShowSuccessToast(message)
        },
        onGetInspectionDetail = { inspectionId ->
            getInspectionDetail(inspectionId)
        },
        onTriggerRetrySubmit = {
            retryPendingSubmit()
        },
        onDeleteInspection = {
            deleteInspection(it)
        },
        onEditInspectionWithStatusComplete = {
            viewModelScope.launch {
                handleOnEditInspectionWithStatusComplete(it)
            }
        }
    )

    init {
        onInit()
    }

    private fun onInit() = viewModelScope.launch {
        syncInspection {
            syncData()
        }
        processGetProfile(getProfileUseCase)
        getTemplateOptions().join()
        getVesselList().join()
        getListTemplate()
        getInspection(page = state.page, false).join()
    }

    private fun handleOnGetData(page: Int, isRefreshData: Boolean) {
        getInspection(page, isRefreshData)
    }

    private fun getInspection(page: Int, isRefreshData: Boolean) = viewModelScope.launch {
        getListInspectionUseCase(
            vesselList = state.vesselList,
            page = page,
            pageSize = Constant.PAGE_SIZE,
            isRefreshData = isRefreshData
        ).collect { result ->
            when (result) {
                is FlowResult.Loading -> {
                    if (!result.showLoading && isRefreshData) delay(500)

                    if (isRefreshData) state.isRefreshing = result.showLoading
                    else if (page == 1) state.isLoading = result.showLoading
                    else state.isLoadingLoadMore = result.showLoading
                }

                is FlowResult.Success -> {
                    state.isLoading = false
                    state.isLoadingLoadMore = false
                    state.hasMoreData = result.data?.data?.isNotEmpty() == true

                    result.data?.let { newData ->
                        state.page += 1
                        state.data = if (isRefreshData) newData else state.data.copy(
                            data = state.data.data + newData.data,
                            total = newData.total
                        )
                    }
                }

                is FlowResult.Error -> {
                    SnackBarController.show(
                        message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                        variant = SnackBarVariant.ERROR
                    )
                }
            }
        }
    }

    private fun getListTemplate() {
        getSimpleListTemplateUseCase().onEach { result ->
            if (result is FlowResult.Error) {
                SnackBarController.show(
                    message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }

            if (result is FlowResult.Success) {
                state.templates = result.data ?: emptyList()
            }
        }.launchIn(viewModelScope)
    }

    private fun startInspection(templateId: String, templateName: String) {
        val inspectionId = Uuid.generate()
        val conducted = DateTimeUtil.getCurrentDateIsoUtc0()
        startInspectionUseCase(
            templateId = templateId,
            inspectionId = inspectionId,
            conductedIso = conducted
        ).onEach {
            when (it) {
                is FlowResult.Loading -> state.isLoadingStartInspection = it.showLoading
                is FlowResult.Success -> processAfterSuccessStartInspection(
                    onCreateLocalData = {
                        createNewInspection(
                            templateId,
                            inspectionId,
                            conductedIso = conducted
                        )
                    },
                    onGoToInspectionAnswer = {
                        state.data = state.data.copy(
                            data = listOf(
                                InspectionModel(
                                    id = inspectionId,
                                    templateId = templateId,
                                    conducted = conducted,
                                    lastUpdated = conducted,
                                    templateName = templateName,
                                    status = InspectionStatus.IN_PROGRESS
                                )
                            ) + state.data.data
                        )
                        goToInspectionAnswer(inspectionId)
                    }
                )

                is FlowResult.Error -> SnackBarController.show(
                    message = it.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }.launchIn(viewModelScope)
    }

    private fun createNewInspection(
        templateId: String,
        inspectionId: String,
        conductedIso: String
    ) {
        createNewInspectionUseCase(templateId, inspectionId, conductedIso).onEach { result ->
            when (result) {
                is FlowResult.Loading -> state.isLoadingStartInspection = result.showLoading
                is FlowResult.Success -> result.data?.let {
                    state.data = state.data.copy(
                        data = listOf(it) + state.data.data
                    )
                    goToInspectionAnswer(it.id)
                }

                is FlowResult.Error -> SnackBarController.show(
                    message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }.launchIn(viewModelScope)
    }

    private fun onShowSuccessToast(message: String) = viewModelScope.launch {
        SnackBarController.show(
            message = message,
            variant = SnackBarVariant.SUCCESS,
            position = SnackBarPosition.BOTTOM
        )
    }

    private fun getInspectionDetail(inspectionId: String) {
        getInspectionDetailUseCase(
            inspectionId = inspectionId,
            templateOptions = state.templateOptions
        ).onEach { result ->
            when (result) {
                is FlowResult.Loading -> state.isLoadingInspectionDetail = result.showLoading
                is FlowResult.Success -> result.data.let {
                    state.onShowInspectionDetailModal(true, it)
                }

                is FlowResult.Error ->
                    SnackBarController.show(
                        message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                        variant = SnackBarVariant.ERROR
                    )
            }
        }.launchIn(viewModelScope)
    }

    private fun getTemplateOptions() = viewModelScope.launch {
        getListTemplateOptionUseCase().collect { result ->
            if (result is FlowResult.Error) SnackBarController.show(
                message = result.error?.message ?: Constant.UNKNOWN_ERROR
            )
            if (result is FlowResult.Success) state.templateOptions = result.data ?: emptyList()
        }
    }

    private fun getVesselList() = viewModelScope.launch {
        getListVesselUseCase().collect { result ->
            if (result is FlowResult.Error) SnackBarController.show(
                message = result.error?.message ?: Constant.UNKNOWN_ERROR
            )
            if (result is FlowResult.Success) state.vesselList = result.data ?: emptyList()
        }
    }

    private fun retryPendingSubmit() = viewModelScope.launch {
        retryPendingSubmitPlatform(
            onRetry = { submitRetryService.trigger() }
        )
    }

    private fun deleteInspection(inspectionId: String) {
        deleteInspectionUseCase(inspectionId).onEach { result ->
            when (result) {
                is FlowResult.Loading -> state.isLoading = result.showLoading
                is FlowResult.Success -> {
                    state.data = state.data.copy(
                        data = state.data.data.filter { it.id != inspectionId }
                    )
                    onShowSuccessToast("Inspection successfully deleted")
                }

                is FlowResult.Error -> SnackBarController.show(
                    message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }.launchIn(viewModelScope)
    }

    private fun syncData() = viewModelScope.launch {
        syncInspectionUseCase().onEach { result ->
            when (result) {
                is FlowResult.Loading -> state.isLoading = result.showLoading
                is FlowResult.Success -> result.data?.removedInspectionIds?.let { removedInspectionIds ->
                    removedInspectionIds.forEach {
                        deleteInspectionUseCase(it).collect()
                    }
                    if (state.data.data.isNotEmpty()) {
                        state.data = state.data.copy(
                            data = state.data.data.filter { it.id !in removedInspectionIds }
                        )
                    }
                }

                is FlowResult.Error -> SnackBarController.show(
                    message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }.launchIn(viewModelScope)
    }

    private suspend fun goToInspectionAnswer(inspectionId: String) {
        _navigationEvent.send(
            InspectionLandingEvent.NavigateToInspectionAnswer(
                inspectionId
            )
        )
    }

    private suspend fun handleOnEditInspectionWithStatusComplete(inspectionId: String) {
        viewModelScope.launch {
            withContext(NonCancellable) {
                deleteInspectionReportsUseCase(inspectionId).collect()
            }
        }
        goToInspectionAnswer(inspectionId)
    }
}

expect suspend fun retryPendingSubmitPlatform(
    onRetry: suspend () -> Unit
)

expect suspend fun syncInspection(
    onSync: suspend () -> Unit
)
